import {
  AddressDto,
  CreateScheduledEventRequestDto,
  ScheduledEventDto,
  ScheduledEventFormType,
  SuggestedScheduledEventDto,
  UpdateScheduledEventRequestDto,
} from '@act/shared/data-transfer-objects';
import { ReactNode } from 'react';
import { DateTime } from 'luxon';
import { scheduledEventDateTimeHelpers } from 'utilities/dateTimeGenerator.util';
import { EventTypeExtended } from './scheduledEventDialogFormTypes';
import { noteHtmlForSave } from './hooks/useAutosave.hook';
import {
  epochCentralToLocalScheduledEventTime,
  normalizeScheduledEventNoteForDefault,
  SCHEDULED_EVENT_ANYTIME,
  SCHEDULED_EVENT_DATE_FORMAT,
} from './scheduledEventForm.util';

export type ScheduledEventSelectOption = {
  key: number;
  label: ReactNode;
  value: number;
};

/** @deprecated Use {@link ScheduledEventSelectOption}. */
export type ScheduledEventAddressOption = ScheduledEventSelectOption;

/** Source for mapping optional prefill into scheduled-event form defaults. */
export type ScheduledEventFormDefaultsSource = {
  scheduledEvent?: ScheduledEventDto;
  suggestedEvent?: SuggestedScheduledEventDto;
};

/**
 * Builds location select options for a scheduled event, including an "Other" sentinel.
 * @param addresses - Member addresses.
 * @returns Address options for the visit location select.
 */
export function buildScheduledEventAddressOptions(
  addresses: AddressDto[] | undefined
): ScheduledEventSelectOption[] {
  const activeAddresses = addresses?.filter(address => !address.deletedAt) ?? [];
  const curatedAddresses = activeAddresses.map(address => ({
    key: address.id,
    label: `${address.line1}, ${address.line2 ?? ''}, ${address.city}, ${address.state} ${address.zip}`,
    value: address.id,
  }));

  return [...curatedAddresses, { key: -1, label: 'Other', value: -1 }];
}

/**
 * Resolves visit location fields from a suggested scheduled event DTO.
 * @param suggestedEvent - LLM suggestion payload.
 * @returns Visit location id and optional free-text other location.
 */
export function resolveSuggestedVisitLocationDefaults(
  suggestedEvent: SuggestedScheduledEventDto
): {
  visitLocationId: number | null | undefined;
  otherLocation: string | null;
} {
  if (suggestedEvent.visitLocationAddressId != null) {
    return {
      visitLocationId: suggestedEvent.visitLocationAddressId,
      otherLocation: null,
    };
  }

  if (suggestedEvent.visitLocation) {
    return {
      visitLocationId: -1,
      otherLocation: suggestedEvent.visitLocation,
    };
  }

  if (suggestedEvent.type === 'visit') {
    return {
      visitLocationId: -1,
      otherLocation: null,
    };
  }

  return {
    visitLocationId: undefined,
    otherLocation: null,
  };
}

/**
 * Resolves visit location fields from an existing scheduled event DTO.
 * @param scheduledEvent - Existing scheduled event.
 * @returns Visit location id and optional free-text other location.
 */
export function resolveScheduledEventVisitLocationDefaults(
  scheduledEvent: ScheduledEventDto
): {
  visitLocationId: number | undefined;
  otherLocation: string | null;
} {
  if (
    scheduledEvent.visitLocation &&
    typeof scheduledEvent.visitLocation === 'object' &&
    'id' in scheduledEvent.visitLocation
  ) {
    return {
      visitLocationId: scheduledEvent.visitLocation.id,
      otherLocation: null,
    };
  }

  if (typeof scheduledEvent.visitLocation === 'string') {
    return {
      visitLocationId: -1,
      otherLocation: scheduledEvent.visitLocation,
    };
  }

  return {
    visitLocationId: undefined,
    otherLocation: null,
  };
}

/**
 * Maps optional scheduled-event or suggestion sources into react-hook-form defaults.
 * @param source - At most one of `scheduledEvent` or `suggestedEvent` should be set.
 * @returns Default values for `ScheduledEventFormType`.
 */
export function mapScheduledEventToFormDefaults(
  source: ScheduledEventFormDefaultsSource = {}
): ScheduledEventFormType {
  const defaultDate = DateTime.now().toFormat(SCHEDULED_EVENT_DATE_FORMAT);
  const { scheduledEvent, suggestedEvent } = source;

  if (suggestedEvent) {
    const suggestedEventTimeLocal = epochCentralToLocalScheduledEventTime(
      suggestedEvent.time
    );
    const visitDefaults = resolveSuggestedVisitLocationDefaults(suggestedEvent);

    return {
      eventType:
        (suggestedEvent.type as ScheduledEventFormType['eventType']) ||
        EventTypeExtended.CALL,
      date: suggestedEvent.date || defaultDate,
      time:
        scheduledEventDateTimeHelpers.getTimeValueFromEpochMillis(
          suggestedEventTimeLocal
        ) || SCHEDULED_EVENT_ANYTIME,
      note: normalizeScheduledEventNoteForDefault(suggestedEvent.note),
      visitLocationId: visitDefaults.visitLocationId,
      otherLocation: visitDefaults.otherLocation,
    };
  }

  if (scheduledEvent) {
    const visitDefaults =
      resolveScheduledEventVisitLocationDefaults(scheduledEvent);

    return {
      eventType: scheduledEvent.type || EventTypeExtended.CALL,
      date: scheduledEvent.date ? scheduledEvent.date : defaultDate,
      time:
        scheduledEventDateTimeHelpers.getTimeValueFromEpochMillis(
          scheduledEvent.time
        ) || SCHEDULED_EVENT_ANYTIME,
      note: normalizeScheduledEventNoteForDefault(scheduledEvent.note),
      visitLocationId: visitDefaults.visitLocationId,
      otherLocation: visitDefaults.otherLocation,
    };
  }

  return {
    eventType: EventTypeExtended.CALL,
    date: defaultDate,
    time: SCHEDULED_EVENT_ANYTIME,
    note: '',
    visitLocationId: undefined,
    otherLocation: null,
  };
}

/**
 * Resolves millis-of-day for time-option generation from form default sources.
 * @param source - Scheduled event and/or suggestion prefill.
 * @returns Millis from local midnight when a time is present.
 */
export function resolveScheduledEventTimeMillisForOptions(
  source: ScheduledEventFormDefaultsSource = {}
): number | undefined {
  const { scheduledEvent, suggestedEvent } = source;

  if (suggestedEvent?.time != null) {
    const suggestedEventTimeLocal = epochCentralToLocalScheduledEventTime(
      suggestedEvent.time
    );
    return suggestedEventTimeLocal != null
      ? scheduledEventDateTimeHelpers.getTimeValueFromEpochMillis(
          suggestedEventTimeLocal
        )
      : undefined;
  }

  if (scheduledEvent?.time != null) {
    return scheduledEventDateTimeHelpers.getTimeValueFromEpochMillis(
      scheduledEvent.time
    );
  }

  return undefined;
}

/**
 * Builds time select options for the scheduled-event form.
 * @param timeMillisOfDay - Millis from local midnight to ensure appears in the list.
 * @returns Select options including the "Anytime" sentinel.
 */
export function buildScheduledEventTimeOptions(
  timeMillisOfDay?: number
): ScheduledEventSelectOption[] {
  return [
    {
      key: SCHEDULED_EVENT_ANYTIME,
      label: 'Anytime',
      value: SCHEDULED_EVENT_ANYTIME,
    },
    ...scheduledEventDateTimeHelpers
      .getTimeOptions(timeMillisOfDay)
      .map(option => ({
        key: option.value,
        label: option.label,
        value: option.value,
      })),
  ];
}

export type BuildScheduledEventSaveBodyParams = {
  form: ScheduledEventFormType;
  suggestedEventId?: string;
};

/**
 * Builds the API request body for creating or updating a scheduled event.
 * Empty-editor HTML (`<p><br></p>` and markup-only content) is sent as `''`
 * so PATCH can clear an existing note without persisting Quill sentinels.
 * @param params - Form values and optional suggestion id.
 * @returns Create/update request DTO.
 */
export function buildScheduledEventSaveBody({
  form,
  suggestedEventId,
}: BuildScheduledEventSaveBodyParams):
  | CreateScheduledEventRequestDto
  | UpdateScheduledEventRequestDto {
  const body: CreateScheduledEventRequestDto | UpdateScheduledEventRequestDto = {
    date: form.date,
    type: form.eventType,
    visitLocationAddressId:
      form.visitLocationId !== -1 ? form.visitLocationId : null,
    visitLocation: form.visitLocationId === -1 ? form.otherLocation : null,
    note: noteHtmlForSave(form.note),
  };

  if (form.time === SCHEDULED_EVENT_ANYTIME) {
    body.time = null;
  } else {
    const date = DateTime.fromFormat(form.date, SCHEDULED_EVENT_DATE_FORMAT);
    body.time = date.plus({ milliseconds: form.time }).toMillis();
  }

  if (suggestedEventId) {
    body.suggestedScheduledEventId = suggestedEventId;
  }

  return body;
}

export type ResolveVisitLocationOnEventTypeChangeParams = {
  eventType: string;
  visitLocationId: number | null | undefined;
  addressOptions: ScheduledEventSelectOption[];
};

/**
 * Resolves visit location field values when the event type changes.
 * @param params - Current event type, visit location, and address options.
 * @returns Partial form updates to apply, if any.
 */
export function resolveVisitLocationOnEventTypeChange({
  eventType,
  visitLocationId,
  addressOptions,
}: ResolveVisitLocationOnEventTypeChangeParams): Partial<ScheduledEventFormType> | null {
  if (eventType !== 'visit') {
    return {
      visitLocationId: undefined,
      otherLocation: null,
    };
  }

  if (!visitLocationId) {
    const firstAddressId = addressOptions[0]?.value;
    if (firstAddressId !== undefined && firstAddressId !== -1) {
      return {
        visitLocationId: firstAddressId,
      };
    }
  }

  return null;
}
